<template>
  <div class="mt-3">
    <div class="row">
      <div class="col-12 col-sm-4">
        <v-text-field
          v-model="collection_address.name"
          class
          :label="$t('name')"
          clearable
          outlined
          dense
          :error-messages="collectionNameErrors"
          @input="$v.collection_address.name.$touch()"
          @blur="$v.collection_address.name.$touch()"
        />
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.company"
          :label="$t('company')"
          clearable
          outlined
          dense
        />
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.email"
          :label="$t('email')"
          clearable
          outlined
          dense
        />
      </div>

      <div class="col-12 col-sm-4">
        <v-autocomplete
          @change="onCollectionCountryChange"
          v-model="collection_address.country"
          :label="getSuggestedRegion('country')"
          item-text="text"
          item-value="index"
          :items="serverData.countries"
          clearable
          outlined
          dense
          :error-messages="collectionCountryErrors"
          @input="$v.collection_address.country.$touch()"
          @blur="$v.collection_address.country.$touch()"
        />
      </div>

      <div class="col-12 col-sm-4">
        <v-combobox
          v-model="collection_address.state"
          :search-input.sync="collection_state_text"
          :items="collectionAddressServerData.states"
          :disabled="isCollectionStatesLoaded"
          :rules="[validationValueCheck('collection_state')]"
          item-text="title"
          item-value="id"
          :label="getSuggestedRegion('state')"
          persistent-hint
          outlined
          clearable
          dense
          :error-messages="collectionStateErrors"
          @input="$v.collection_address.state.$touch()"
          @blur="$v.collection_address.state.$touch()"
          @change="onCollectionStateChange"
          @keyup="updateValue('collection_state')"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  No results matching "<strong>{{
                    collection_state_text
                  }}</strong
                  >".
                  <button
                    class="btn btn-info pl-2"
                    @click="
                      createNewItem(
                        'state',
                        collection_address.country,
                        onCollectionCountryChange,
                        collection_state_text
                      )
                    "
                  >
                    Create new
                  </button>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </div>
      <div class="col-12 col-sm-4">
        <v-combobox
          v-model="collection_address.city"
          :search-input.sync="collection_city_text"
          :items="collectionAddressServerData.cities"
          :rules="[validationValueCheck('collection_city')]"
          :disabled="isCollectionCitiesLoaded"
          item-text="title"
          item-value="id"
          :label="getSuggestedRegion('city')"
          persistent-hint
          outlined
          dense
          clearable
          :error-messages="collectionCityErrors"
          @input="$v.collection_address.city.$touch()"
          @blur="$v.collection_address.city.$touch()"
          @change="onCollectionCityChange"
          @keyup="updateValue('collection_city')"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  No results matching "<strong>{{
                    collection_city_text
                  }}</strong
                  >".
                  <button
                    class="btn btn-info pl-2"
                    @click="
                      createNewItem(
                        'city',
                        collection_address.state.id,
                        onCollectionStateChange,
                        collection_city_text
                      )
                    "
                  >
                    Create new
                  </button>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </div>
      <div class="col-12 col-sm-4">
        <v-combobox
          v-model="collection_address.area"
          :search-input.sync="collection_area_text"
          :items="collectionAddressServerData.areas"
          :rules="[validationValueCheck('collection_area')]"
          item-text="title"
          item-value="id"
          :label="getSuggestedRegion('area')"
          persistent-hint
          outlined
          :disabled="isCollectionAreasLoaded"
          dense
          @keyup="updateValue('collection_area')"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  No results matching "<strong>{{
                    collection_area_text
                  }}</strong
                  >".
                  <button
                    class="btn btn-info pl-2"
                    @click="
                      createNewItem(
                        'area',
                        collection_address.city.id,
                        onCollectionCityChange,
                        collection_area_text
                      )
                    "
                  >
                    Create new
                  </button>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.address"
          :label="$t('address')"
          clearable
          outlined
          dense
          :error-messages="collectionAddressErrors"
          @input="$v.collection_address.address.$touch()"
          @blur="$v.collection_address.address.$touch()"
        />
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.address_2"
          :label="$t('address_2')"
          clearable
          outlined
          dense
        />
      </div>
      <div class="col-12 col-sm-4">
        <VueTelInputVuetify
          v-model="collection_address.phone"
          :label="$t('phone')"
          type="number"
          clearable
          outlined
          dense
          :inputOptions="{ showDialCode: false, tabindex: 0 }"
          mode="international"
          disabledFetchingCountry
          defaultCountry="AE"
          :error-messages="collectionPhoneErrors"
          @input="$v.collection_address.phone.$touch()"
          @blur="$v.collection_address.phone.$touch()"
        />
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.postal_code"
          :label="$t('postal_code')"
          clearable
          outlined
          dense
        />
      </div>
      <div class="col-12 col-sm-4">
        <v-text-field
          class
          v-model="collection_address.short_address"
          :label="$t('short_address')"
          clearable
          outlined
          dense
          maxlength="8"
          counter="8"
          @paste="handlePaste"
        />
      </div>
    </div>
    <CreateLocation
      :location-id="getLocationId"
      :location-type="getLocationType"
      :location-function="getLocationFunc"
      :locationSetValue="setTextValue"
      :initValue="getLocationInitValue"
      ref="location"
    />
  </div>
</template>
<script>
import CreateLocation from "@/own/components/orderManagement/returned/CreateReturned/CreateLocation.vue";

import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import ApiService from "@/core/services/api.service";
import VueTelInputVuetify from "vue-tel-input-vuetify/lib/vue-tel-input-vuetify.vue";

export default {
  name: "CollectionAddress",
  mixins: [validationMixin],
  props: ["pageLoader"],
  components: { CreateLocation, VueTelInputVuetify },
  validations() {
    return {
      collection_address: {
        name: { required },
        address: { required },
        city: { required },
        country: { required },
        phone: { required },
        state: { required },
      },
    };
  },
  data: () => {
    return {
      collection_address: {
        name: null,
        company: null,
        address: null,
        area: null,
        city: null,
        country: null,
        phone: null,
        state: null,
        // building_no: null,
        // unit_no: null,
        // additional_no: null,
        postal_code: null,
        email: null,
        short_address: null,
      },

      location_type: null,
      locaiton_id: null,
      location_function: null,

      collection_state_text: null,
      collection_city_text: null,
      collection_area_text: null,

      collectionAddressServerData: {},
    };
  },
  computed: {
    getLocationId() {
      return this.locaiton_id;
    },
    getLocationType() {
      return this.location_type;
    },
    getLocationFunc() {
      return this.location_function;
    },
    collectionNameErrors: function () {
      return this.handleFormValidation("name", this, "collection_address");
    },
    collectionAddressErrors: function () {
      return this.handleFormValidation("address", this, "collection_address");
    },
    collectionCityErrors: function () {
      return this.handleFormValidation("city", this, "collection_address");
    },
    collectionPhoneErrors: function () {
      return this.handleFormValidation("phone", this, "collection_address");
    },
    collectionMobileErrors: function () {
      return this.handleFormValidation("mobile", this, "collection_address");
    },
    collectionCountryErrors: function () {
      return this.handleFormValidation("country", this, "collection_address");
    },
    collectionStateErrors: function () {
      return this.handleFormValidation("state", this, "collection_address");
    },
    // collectionBuildingNoErrors: function () {
    //   return this.handleFormValidation(
    //     "building_no",
    //     this,
    //     "collection_address"
    //   );
    // },
    // collectionUnitNoErrors: function () {
    //   return this.handleFormValidation("unit_no", this, "collection_address");
    // },
    // collectionAdditionalNoErrors: function () {
    //   return this.handleFormValidation(
    //     "additional_no",
    //     this,
    //     "collection_address"
    //   );
    // },
    getLocationInitValue() {
      return this.location_initValue;
    },
    serverData: function () {
      return this.$store.getters.getPENDINGRETURNSServerData;
    },
    isCollectionAreasLoaded: function () {
      return !this.collectionAddressServerData.areas;
    },
    isCollectionCitiesLoaded: function () {
      return !this.collectionAddressServerData.cities;
    },
    isCollectionStatesLoaded: function () {
      return !this.collectionAddressServerData.states;
    },
    isFormValid: function () {
      return !this.$v.$invalid;
    },
    editData() {
      return this.$store.getters.getPENDINGRETURNSEditData;
    },
  },
  methods: {
    handlePaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        "text"
      );
      const cleanedText = pastedText.trim().substring(0, 8);
      this.collection_address.short_address = cleanedText;
    },
    async onCollectionCountryChange(val, safe = false, is_id = false) {
      if (val) {
        this.pageLoader(true);
        let data = is_id ? { country: val } : { country: val };
        await ApiService.post("/address/states/search", data)
          .then((response) => {
            this.collectionAddressServerData.states = response.data.states;
            if (!safe) {
              //
            }
            let i = { ...this.collectionAddressServerData };
            this.collectionAddressServerData = i;
            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);

            this.dialog = false;
          });
      }
    },
    async onCollectionStateChange(val, safe = false, is_id = false) {
      if (val) {
        let data = { state: null };
        if (is_id) {
          data.state = val;
        } else {
          let id = null;
          try {
            id = this.collectionAddressServerData.states.filter(
              (state) => state.title == val.title
            )[0].id;
          } catch {
            return;
          }
          if (!id) {
            return;
          }
          data.state = id;
        }
        this.pageLoader(true);

        await ApiService.post("/address/cities/search", data)
          .then((response) => {
            this.collectionAddressServerData.cities = response.data.cities;

            if (!safe) {
              //
            }
            let i = { ...this.collectionAddressServerData };
            this.collectionAddressServerData = i;

            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);

            this.dialog = false;
          });
      }
    },
    async onCollectionCityChange(val, safe = false, is_id = false) {
      if (val) {
        let data = { city: null };
        if (is_id) {
          data.city = val;
        } else {
          let id = null;
          try {
            id = this.collectionAddressServerData.cities.filter(
              (city) => city.title == val.title
            )[0].id;
          } catch {
            return;
          }
          if (!id) {
            return;
          }

          data.city = id;
        }
        this.pageLoader(true);
        await ApiService.post("/address/areas/search", data)
          .then((response) => {
            this.collectionAddressServerData.areas = response.data.areas;
            if (!safe) {
              // this.collection_address.area = null;
            }
            let i = { ...this.collectionAddressServerData };
            this.collectionAddressServerData = i;
            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);
            this.dialog = false;
          });
      }
    },
    validationValueCheck(type) {
      let validation = true;
      switch (type) {
        case "collection_state":
          try {
            if (
              this.collectionAddressServerData.states.filter(
                (state) => state.title == this.collection_state_text
              ).length === 0
            ) {
              validation = "Please, select valid state!";
              // this.serverData.cities = null;
              // this.serverData.areas = null;
            }
          } catch {
            break;
          }

          break;

        case "collection_city":
          try {
            if (
              this.collectionAddressServerData.cities.filter(
                (city) => city.title == this.collection_city_text
              ).length === 0
            ) {
              validation = "Please, select valid city!";
              // this.serverData.areas = null;
            }
          } catch {
            break;
          }

          break;

        case "collection_area":
          try {
            if (
              this.collectionAddressServerData.areas.filter(
                (area) => area.title == this.collection_area_text
              ).length === 0
            ) {
              validation = "Please, select valid area!";
            }
          } catch {
            break;
          }

          break;
      }
      return validation;
    },
    updateValue(type) {
      let timer;
      const waitTime = 1250;
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.onBlurValueCheck(type);
      }, waitTime);
    },
    onBlurValueCheck(type) {
      switch (type) {
        case "collection_state":
          if (
            this.collectionAddressServerData.states.filter(
              (state) => state.title == this.collection_state_text
            ).length === 0
          ) {
            setTimeout(() => {
              // this.state_text = "";
              // this.serverData.cities = null;
              // this.serverData.areas = null;
            }, 2000);
          } else {
            this.onCollectionStateChange(
              this.collectionAddressServerData.states.filter(
                (state) => state.title == this.collection_state_text
              )[0]
            );
          }

          break;
        case "collection_city":
          if (
            this.collectionAddressServerData.cities.filter(
              (city) => city.title == this.collection_city_text
            ).length === 0
          ) {
            setTimeout(() => {
              // this.city_text = "";
              // this.serverData.areas = null;
            }, 2000);
          } else {
            this.onCollectionCityChange(
              this.collectionAddressServerData.cities.filter(
                (city) => city.title == this.collection_city_text
              )[0]
            );
          }
          break;
        case "collection_area":
          if (
            this.collectionAddressServerData.areas.filter(
              (area) => area.title == this.collection_area_text
            ).length === 0
          ) {
            setTimeout(() => {
              // this.area_text = "";
            }, 2000);
          } else {
            this.collection_address.area =
              this.collectionAddressServerData.areas.filter(
                (area) => area.title == this.collection_area_text
              )[0].title;
          }
          break;
      }
    },
    async setTextValue(type, value = "") {
      switch (type) {
        case "collection_state":
          this.collection_state_text = value;

          if (value !== "") {
            this.collection_address.state =
              this.collectionAddressServerData.states.filter(
                (state) => state.title == this.collection_state_text
              )[0];
            await this.onCollectionStateChange(
              this.collectionAddressServerData.states.filter(
                (state) => state.title == this.collection_state_text
              )[0]
            );
          }

          break;
        case "collection_city":
          this.city_text = value;

          if (value !== "") {
            this.collection_address.city =
              this.collectionAddressServerData.cities.filter(
                (city) => city.title == this.city_text
              )[0];
            await this.onCollectionCityChange(
              this.collectionAddressServerData.cities.filter(
                (city) => city.title == this.city_text
              )[0]
            );
          }
          break;
        case "collection_area":
          this.collection_area_text = value;
          break;
      }
    },
    handleFormValidation(fieldName, vueObj, dataName = "formData") {
      const errors = [];
      if (!vueObj.$v[`${dataName}`][fieldName].$dirty) return errors;
      if ("required" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      if ("maxValue" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].maxValue &&
          errors.push("This field must be greater than 0");
      }
      if ("url" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].url &&
          errors.push(this.$t("validation_this_url_is_invalid"));
      }
      if ("numeric" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].numeric &&
          errors.push(this.$t("validation_this_must_be_a_number"));
      }
      if ("email" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].email &&
          errors.push(this.$t("validation_this_email_address_is_invalid"));
      }
      return errors;
    },
    createNewItem(type, id, func, value = "") {
      this.location_type = type;
      this.locaiton_id = id;
      this.location_function = func;
      this.location_initValue = value;
      this.$nextTick(() => {
        this.$refs.location.toggleModal();
      });
    },
    checkErrors() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }
    },
    clearFields() {
      this.collection_address = {
        name: null,
        company: null,
        address: null,
        area: null,
        city: null,
        country: null,
        phone: null,
        state: null,
        // building_no: null,
        // unit_no: null,
        // additional_no: null,
        postal_code: null,
        email: null,
        short_address: null,
      };
      this.location_type = null;
      this.locaiton_id = null;
      this.location_function = null;

      this.collection_state_text = null;
      this.collection_city_text = null;
      this.collection_area_text = null;

      this.collectionAddressServerData = {};

      //reset validation
      this.$v.$reset();
    },
    async setDeafultCollectionAddress() {
      this.collection_address.country = 229;
      let state = null;
      let city = null;

      await this.onCollectionCountryChange(229);
      state = this.collectionAddressServerData.states.find(
        (item) => item.id === 4182
      );

      await this.onCollectionStateChange(state, true);
      city = this.collectionAddressServerData.cities.find(
        (item) => item.id === 48468
      );
      await this.onCollectionCityChange(city, true);

      this.collection_address = {
        ...this.collection_address,
        state,
        city,
      };
    },
    async getEditData() {
      const data = this.editData.collectionAddress;
      let state = null;
      let city = null;
      let area = null;

      await this.onCollectionCountryChange(data.country);
      try {
        if (data.state) {
          state = this.collectionAddressServerData?.states.find(
            (item) => item.id === data.state
          );
          await this.onCollectionStateChange(state, true);
        }
        if (data.city) {
          city = this.collectionAddressServerData?.cities.find(
            (item) => item.id === data.city
          );
          await this.onCollectionCityChange(city, true);
        }
        if (data.area) {
          // await this.onCollectionCityChange(city, true);
          area = this.collectionAddressServerData?.areas.find(
            (item) => item.id === data.area
          );
        }
      } catch {
        //
      }

      this.collection_address = {
        ...this.editData.collectionAddress,
        state,
        city,
        area,
      };
      // console.log(state);
    },
    getSuggestedRegion(type) {
      const typeCapitalize = type.charAt(0).toUpperCase() + type.slice(1);

      if (
        this.serverData.return_management &&
        this.serverData.return_management[`c_${type}`]
      ) {
        return `${typeCapitalize} (Suggested: ${
          this.serverData.return_management[`c_${type}`]
        })`;
      } else {
        return typeCapitalize;
      }
    },
  },
};
</script>
